import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
  UserIcon,
  EnvelopeIcon,
  LockClosedIcon,
  GlobeAltIcon,
  MapPinIcon,
  BuildingOffice2Icon,
  PhoneIcon,
  HomeIcon,
  HashtagIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';

import * as API from '../../api';

/**
 * Account details / billing form. Premium redesign.
 *
 * Logic preserved verbatim: API.Auth() prefill, submitAuthInfo ->
 * API.ChangeUserInfo, the password-change logout redirect, 422 field-error
 * mapping, success snackbar, and every input's `name` attribute (read by
 * FormData on submit).
 */
const Edit = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [auth, setAuth] = React.useState({});
  const [loadingAuth, setLoadingAuth] = React.useState(false);
  const [loadingSubmit, setLoadingSubmit] = React.useState(false);
  const [authInfoHelper, setAuthInfoHelper] = React.useState({ type: '', message: '' });

  const [nameHelper, setNameHelper] = React.useState('');
  const [emailHelper, setEmailHelper] = React.useState('');
  const [password_oldHelper, setPasswordOldHelper] = React.useState('');
  const [passwordHelper, setPasswordHelper] = React.useState('');
  const [password_confirmationHelper, setPasswordConfirmationHelper] = React.useState('');

  const submitAuthInfo = (event) => {
    event.preventDefault();
    setAuthInfoHelper({ type: '', message: '' });
    setNameHelper('');
    setEmailHelper('');
    setPasswordOldHelper('');
    setPasswordHelper('');
    setPasswordConfirmationHelper('');
    setLoadingSubmit(true);

    const formData = new FormData(event.target);
    const form = {
      name: formData.get('name'),
      email: formData.get('email'),
      oldPassword: formData.get('password_old'),
      password: formData.get('password'),
      password_confirmation: formData.get('password_confirmation'),
      country: formData.get('country'),
      city: formData.get('city'),
      company: formData.get('company'),
      phone: formData.get('phone'),
      address: formData.get('address'),
      code_postal: formData.get('code_postal'),
    };

    if (form.password && form.password !== form.password_confirmation) {
      setPasswordConfirmationHelper(t('account.passwordMismatch'));
    }

    API.ChangeUserInfo(form)
      .then(() => {
        setAuthInfoHelper({ type: 'success', message: t('account.saveSuccess') });
        enqueueSnackbar(t('account.saveSuccess'), { variant: 'success' });
        if (form.oldPassword !== form.password) {
          history.push('/logout?target=login');
        }
      })
      .catch((err) => {
        if (err.status === 422) {
          setAuthInfoHelper({ type: 'error', message: err.data.message });
          enqueueSnackbar(err.data.message, { variant: 'error' });
          if (err.data.errors.name) setNameHelper(err.data.errors.name);
          if (err.data.errors.email) setEmailHelper(err.data.errors.email);
          if (err.data.errors.oldPassword) setPasswordOldHelper(err.data.errors.oldPassword);
          if (err.data.errors.password) setPasswordHelper(err.data.errors.password);
          if (err.data.errors.password_confirmation)
            setPasswordConfirmationHelper(err.data.errors.password_confirmation);
        }
      })
      .then(() => setLoadingSubmit(false));
  };

  React.useEffect(() => {
    setLoadingAuth(true);
    API.Auth()
      .then((res) => setAuth(res.user))
      .catch(() => {})
      .then(() => setLoadingAuth(false));
  }, [0]);

  if (loadingAuth) {
    return (
      <div className="flex h-60 items-center justify-center rounded-2xl border border-slate-100 bg-white shadow-card">
        <span className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-brand-500/30 border-t-brand-500" />
      </div>
    );
  }

  return (
    <form onSubmit={submitAuthInfo} method="POST" className="space-y-6">
      {authInfoHelper.message ? (
        <div
          className={`flex items-start gap-2 rounded-xl border px-4 py-3 text-sm ${
            authInfoHelper.type === 'success'
              ? 'border-emerald-100 bg-emerald-50 text-emerald-700'
              : 'border-rose-100 bg-rose-50 text-rose-600'
          }`}
        >
          {authInfoHelper.type === 'success' ? (
            <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0" />
          ) : (
            <ExclamationCircleIcon className="mt-0.5 h-4 w-4 shrink-0" />
          )}
          <div dangerouslySetInnerHTML={{ __html: authInfoHelper.message }} />
        </div>
      ) : null}

      {/* Account details */}
      <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-card sm:p-6">
        <h2 className="mb-5 font-display text-lg font-semibold text-ink">{t('account.detailsTitle')}</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field name="name" defaultValue={auth.name} label={t('account.fieldName')} icon={UserIcon} error={nameHelper} />
          <Field name="email" type="email" defaultValue={auth.email} label={t('account.fieldEmail')} icon={EnvelopeIcon} error={emailHelper} />
          <Field name="password_old" type="password" label={t('account.fieldOldPassword')} icon={LockClosedIcon} error={password_oldHelper} className="sm:col-span-2" />
          <Field name="password" type="password" label={t('account.fieldNewPassword')} icon={LockClosedIcon} error={passwordHelper} />
          <Field name="password_confirmation" type="password" label={t('account.fieldConfirmPassword')} icon={LockClosedIcon} error={password_confirmationHelper} />
        </div>
      </section>

      {/* Billing */}
      <section className="rounded-2xl border border-slate-100 bg-white p-5 shadow-card sm:p-6">
        <h2 className="mb-5 font-display text-lg font-semibold text-ink">{t('account.billingTitle')}</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field name="country" defaultValue={auth.country} label={t('account.fieldCountry')} icon={GlobeAltIcon} />
          <Field name="city" defaultValue={auth.city} label={t('account.fieldCity')} icon={MapPinIcon} />
          <Field name="company" defaultValue={auth.company} label={t('account.fieldCompany')} icon={BuildingOffice2Icon} />
          <Field name="phone" type="tel" defaultValue={auth.phone} label={t('account.fieldPhone')} icon={PhoneIcon} />
          <Field name="address" defaultValue={auth.address} label={t('account.fieldAddress')} icon={HomeIcon} />
          <Field name="code_postal" defaultValue={auth.code_postal} label={t('account.fieldPostal')} icon={HashtagIcon} />
        </div>
      </section>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={loadingSubmit}
          className="flex min-w-[160px] items-center justify-center rounded-xl bg-accent-500 px-6 py-3 text-sm font-bold text-brand-900 shadow-card transition-all duration-200 hover:bg-accent-400 hover:shadow-card-hover disabled:opacity-60"
        >
          {loadingSubmit ? (
            <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-brand-900/30 border-t-brand-900" />
          ) : (
            t('account.save')
          )}
        </button>
      </div>
    </form>
  );
};

function Field({ name, type = 'text', defaultValue, label, icon: Icon, error, className = '' }) {
  return (
    <div className={className}>
      <label className="mb-1.5 block text-sm font-medium text-ink">{label}</label>
      <div className="relative">
        <input
          name={name}
          type={type}
          defaultValue={defaultValue ?? ''}
          placeholder={label}
          className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-3.5 pr-10 text-sm text-ink placeholder:text-ink-subtle focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100 rtl:pl-10 rtl:pr-3.5"
        />
        {Icon ? (
          <Icon className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle rtl:right-auto rtl:left-3" />
        ) : null}
      </div>
      {error ? <p className="mt-1 text-xs text-rose-500">{error}</p> : null}
    </div>
  );
}

export default Edit;
